import {
  Button,
  Card,
  EmptyState,
  LoadingRegion,
  SEATING_CONFIGURATION_STATES,
  Status,
  presentation,
} from '@gatecheckpro/design-system';
import { ApiRefusal } from '@gatecheckpro/api-client';
import type {
  OrganizerEvent,
  SeatingConfigurationDetail,
  SeatingAvailabilitySummary,
  SeatingConfigurationDocumentView,
  SeatingConfigurationLifecycle,
  SeatingConfigurationView,
  SeatingInventory,
  VenueMapVersionTopology,
} from '@gatecheckpro/contracts';
import { useCommand } from '../../lib/useCommand.ts';
import { useResource } from '../../lib/useResource.ts';
import { FailureNotice } from '../../lib/refusal.tsx';
import { SeatingConfigurationEditor } from './SeatingConfigurationEditor.tsx';
import { SeatingCounts, SeatingRoomCanvas, useSeatDetail } from './SeatingRoom.tsx';

/**
 * Finds this session's seating configuration, or offers to start one.
 *
 * A session has zero or one configuration, so there is nothing to list and
 * nothing to name twice. Absence is a `RESOURCE_NOT_FOUND`, which is a fact
 * about this night rather than a failure — so it renders as an invitation, not
 * as an error.
 */
export function SeatingConfiguration({
  base,
  event,
  eventSessionId,
  chosenMapVersionId,
  draftLabel,
  inventory,
}: {
  readonly base: string;
  readonly event: OrganizerEvent;
  readonly eventSessionId: string;
  readonly chosenMapVersionId: string;
  readonly draftLabel: string;
  readonly inventory: readonly SeatingInventory[];
}) {
  const configuration = useResource<SeatingConfigurationDetail>(
    `${base}/event-sessions/${eventSessionId}/seating`,
  );
  const command = useCommand<SeatingConfigurationDetail>();

  if (configuration.resource.status === 'loading') {
    return <LoadingRegion label="Loading this session's seating" />;
  }

  // Not-found is the ordinary state of a night nobody has set up yet.
  const missing =
    configuration.resource.status === 'failed' &&
    configuration.resource.error instanceof ApiRefusal &&
    configuration.resource.error.code === 'RESOURCE_NOT_FOUND';

  if (missing) {
    return (
      <Card title="Reserved seating" eyebrow={draftLabel}>
        <div className="gc-stack">
          <EmptyState title="This session has no seating set up">
            Choose an approved version of the room above, then start here. What sells on this night
            is decided per session, so a three-night run is set up three times.
          </EmptyState>
          <FailureNotice error={command.error} />
          <div>
            <Button
              variant="primary"
              busy={command.busy}
              disabled={chosenMapVersionId === ''}
              title={
                chosenMapVersionId === '' ? 'Choose an approved map version first.' : undefined
              }
              onClick={() =>
                void command
                  .run(`${base}/event-sessions/${eventSessionId}/seating`, {
                    method: 'POST',
                    body: { displayName: 'Reserved seating' },
                  })
                  .then(async (created) => {
                    if (created === null) return;
                    await command.run(
                      `${base}/seating-configurations/${created.seatingConfigurationId}/versions`,
                      { method: 'POST', body: { venueMapVersionId: chosenMapVersionId } },
                    );
                    configuration.reload();
                  })
              }
            >
              Set up reserved seating
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  if (configuration.resource.status === 'failed') {
    return <FailureNotice error={configuration.resource.error} />;
  }

  const detail = configuration.resource.data;
  const open = detail.draftVersion ?? detail.activeVersion;

  return (
    <Card title="Reserved seating" eyebrow={detail.displayName}>
      <div className="gc-stack">
        <FailureNotice error={command.error} />

        {detail.newerVenueMapVersionAvailable && (
          <p className="gc-muted" data-testid="newer-map-version">
            A newer version of this room has been approved since. This night keeps the one it was
            set up against — moving it needs a migration, which this release does not have.
          </p>
        )}

        {/*
          A night that is already selling still needs revising, so this is
          offered whenever there is no open draft — not only when nothing has
          ever been configured. Without it a configuration became permanent the
          moment it went live, which is not what "a later version supersedes it"
          is supposed to mean.
        */}
        {detail.draftVersion === null && (
          <div className="gc-stack">
            {detail.activeVersion === null && (
              <EmptyState title="No open version">
                Every version of this seating has been replaced or discarded. Start a new draft to
                change what sells.
              </EmptyState>
            )}
            <div>
              <Button
                variant="primary"
                busy={command.busy}
                onClick={() =>
                  void command
                    .run(
                      `${base}/seating-configurations/${detail.seatingConfigurationId}/versions`,
                      {
                        method: 'POST',
                        body: { venueMapVersionId: detail.pinnedVenueMapVersionId },
                      },
                    )
                    .then((result) => {
                      if (result !== null) configuration.reload();
                    })
                }
              >
                Start a draft
              </Button>
            </div>
          </div>
        )}

        {open !== null && (
          <SeatingVersion
            base={base}
            event={event}
            eventSessionId={eventSessionId}
            versionId={open.configurationVersionId}
            inventory={inventory}
            onChanged={() => configuration.reload()}
          />
        )}

        {detail.activeVersion !== null && detail.draftVersion !== null && (
          <p className="gc-muted">
            Version {detail.activeVersion.versionNumber} is selling while version{' '}
            {detail.draftVersion.versionNumber} is being drafted.
          </p>
        )}
      </div>
    </Card>
  );
}

/** One version, loaded with its live projection. */
function SeatingVersion({
  base,
  event,
  eventSessionId,
  versionId,
  inventory,
  onChanged,
}: {
  readonly base: string;
  readonly event: OrganizerEvent;
  readonly eventSessionId: string;
  readonly versionId: string;
  readonly inventory: readonly SeatingInventory[];
  readonly onChanged: () => void;
}) {
  const versionPath = `${base}/seating-configuration-versions/${versionId}`;
  /**
   * Two reads, deliberately.
   *
   * The lean view carries lifecycle and the server's aggregates, and is what
   * every room view needs. The full static document is fetched only for a
   * draft somebody is actually editing, because it is the authoring transfer —
   * around 27 MiB at the contract's ceiling — and a whole-document save cannot
   * be made safely without it.
   */
  const view = useResource<SeatingConfigurationView>(versionPath);
  const editable =
    view.resource.status === 'loaded' && view.resource.data.version.lifecycle === 'DRAFT';
  const document = useResource<SeatingConfigurationDocumentView>(
    editable ? `${versionPath}/document` : null,
  );

  if (view.resource.status === 'loading')
    return <LoadingRegion label="Loading the configuration" />;
  if (view.resource.status === 'failed') return <FailureNotice error={view.resource.error} />;
  if (!editable) {
    return (
      <SeatingConfigurationReadOnly
        base={base}
        versionPath={versionPath}
        venueMapVersionId={view.resource.data.version.venueMapVersionId}
        summary={view.resource.data.summary}
        lifecycle={view.resource.data.version.lifecycle}
      />
    );
  }
  if (document.resource.status === 'loading') return <LoadingRegion label="Loading what sells" />;
  if (document.resource.status === 'failed')
    return <FailureNotice error={document.resource.error} />;

  return (
    <SeatingConfigurationEditor
      key={document.resource.data.configurationDigest}
      base={base}
      versionPath={versionPath}
      event={event}
      eventSessionId={eventSessionId}
      loaded={document.resource.data}
      summary={view.resource.data.summary}
      inventory={inventory}
      onChanged={() => {
        // The lean view only. Its counts move on every save, and it is small.
        // Re-reading the document would fetch the authoring transfer a second
        // time to be told what the browser just sent — around 27 MiB at the
        // contract's ceiling, on every save. The editor already holds the
        // document the server accepted, and the digest it must save against
        // next came back with the write.
        view.reload();
        onChanged();
      }}
    />
  );
}

/**
 * A configuration nobody is editing.
 *
 * Live and replaced versions are read, not opened, so they never fetch the
 * static document. The counts are the server's and the state is stated in its
 * own words, which is all an organizer needs from a night that has stopped
 * changing.
 */
function SeatingConfigurationReadOnly({
  base,
  versionPath,
  venueMapVersionId,
  summary,
  lifecycle,
}: {
  readonly base: string;
  readonly versionPath: string;
  readonly venueMapVersionId: string;
  readonly summary: SeatingAvailabilitySummary;
  readonly lifecycle: SeatingConfigurationLifecycle;
}) {
  const state = presentation(SEATING_CONFIGURATION_STATES, lifecycle);
  const map = useResource<VenueMapVersionTopology>(
    `${base}/venue-map-versions/${venueMapVersionId}`,
  );
  const { overlay, requestDetail } = useSeatDetail(versionPath);

  return (
    <div className="gc-stack gc-stack--tight">
      <div className="gc-row gc-row--baseline">
        <Status tone={state.tone} glyph={state.glyph} label={state.label} title={state.meaning} />
      </div>
      <p className="gc-muted" data-testid="seating-draft-state">
        {state.meaning}
      </p>
      <SeatingCounts summary={summary} />
      {/*
        A night that has gone live is exactly when somebody wants to look at the
        room: which chairs are being bought, which are gone. Read-only means the
        commercial terms cannot change, not that the room stops being worth
        seeing — and the live state comes from the same bounded ask the editor
        makes, so no seat is dressed by anything but the server's answer.
      */}
      {map.resource.status === 'loading' && <LoadingRegion label="Loading the room" />}
      {map.resource.status === 'failed' && <FailureNotice error={map.resource.error} />}
      {map.resource.status === 'loaded' && (
        <SeatingRoomCanvas
          mapView={map.resource.data}
          summary={summary}
          overlay={overlay}
          onDrawnSeatsChange={requestDetail}
        />
      )}
      <p className="gc-muted">
        This configuration is live and cannot change. To change what sells, start a new draft.
      </p>
    </div>
  );
}
